import { useState, useEffect, useRef, useCallback } from 'react'
import { useMessages } from '@/hooks/messaging'
import { useWebSocketContext } from '@/providers/websocket-provider'
import { useAuth } from '@/lib/auth'
import { UserAvatar } from '@/components/UserAvatar'

interface ChatWindowProps {
  conversationId: number
}

export function ChatWindow({ conversationId }: ChatWindowProps) {
  const { user } = useAuth()
  const { data: messages, isLoading } = useMessages(conversationId)
  const {
    isConnected,
    sendMessage: sendWebSocketMessage,
    markAsRead,
    sendTypingIndicator,
    typingUsers,
    queueMessage,
    getPendingForConversation,
  } = useWebSocketContext()

  const [newMessage, setNewMessage] = useState('')
  const [isSending, setIsSending] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Mark conversation as read when opened
  useEffect(() => {
    if (isConnected) {
      markAsRead(conversationId)
    }
  }, [conversationId, markAsRead, isConnected])

  // Handle typing indicator
  const handleTyping = useCallback(() => {
    // Send typing indicator
    if (isConnected) {
      sendTypingIndicator(conversationId, true)

      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }

      // Set timeout to stop typing indicator
      typingTimeoutRef.current = setTimeout(() => {
        sendTypingIndicator(conversationId, false)
      }, 2000)
    }
  }, [conversationId, isConnected, sendTypingIndicator])

  // Clean up typing indicator on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
        if (isConnected) {
          sendTypingIndicator(conversationId, false)
        }
      }
    }
  }, [conversationId, isConnected, sendTypingIndicator])

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newMessage.trim() || isSending) return

    const messageContent = newMessage.trim()
    setNewMessage('')
    setIsSending(true)

    if (isConnected) {
      // WebSocket is connected - send directly
      sendWebSocketMessage(conversationId, messageContent)
    } else {
      // WebSocket is disconnected - queue message locally
      // It will be auto-sent when connection is restored
      queueMessage(conversationId, messageContent)
      console.log('WebSocket offline - message queued for later delivery')
    }

    // Stop typing indicator
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current)
      if (isConnected) {
        sendTypingIndicator(conversationId, false)
      }
    }

    // Reset sending state after a short delay
    setTimeout(() => {
      setIsSending(false)
    }, 500)
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-muted-foreground">Loading messages...</div>
      </div>
    )
  }

  return (
    <div className="flex h-[calc(100vh-200px)] flex-col">
      {/* Connection Status Bar */}
      {(() => {
        const pendingCount = getPendingForConversation(conversationId).length
        return (
          <div
            className={`px-4 py-2 text-xs font-medium ${
              isConnected ? 'bg-primary/10 text-primary' : 'bg-accent/10 text-accent'
            }`}
          >
            {isConnected
              ? '🟢 Connected'
              : `📡 Offline Mode - ${pendingCount} message${pendingCount !== 1 ? 's' : ''} queued for sending`}
          </div>
        )
      })()}

      {/* Messages */}
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        {messages?.length === 0 ? (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            No messages yet. Start the conversation!
          </div>
        ) : (
          messages?.map((message) => {
            const isMe = message.sender_id === user?.id
            const senderAvatar =
              (message as { sender_avatar_url?: string | null }).sender_avatar_url ??
              (isMe ? (user?.profile?.avatar_url ?? null) : undefined)

            return (
              <div
                key={message.id}
                className={`flex items-end gap-2 ${isMe ? 'justify-end' : 'justify-start'}`}
              >
                {!isMe && (
                  <UserAvatar
                    size="sm"
                    username={message.sender_username}
                    avatarUrl={senderAvatar}
                    className="self-end"
                  />
                )}
                <div
                  className={`max-w-xs rounded-lg px-4 py-2 ${
                    isMe ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'
                  }`}
                >
                  <div className="mb-1 text-xs font-semibold opacity-75">
                    {message.sender_username}
                  </div>
                  <div className="break-words">{message.content}</div>
                  <div className="mt-1 text-xs opacity-70">
                    {new Date(message.created_at).toLocaleTimeString()}
                  </div>
                </div>
                {isMe && (
                  <UserAvatar
                    size="sm"
                    username={message.sender_username}
                    avatarUrl={senderAvatar}
                    className="self-end"
                  />
                )}
              </div>
            )
          })
        )}

        {/* Typing Indicators */}
        {typingUsers.length > 0 && (
          <div className="flex items-center gap-2 text-sm italic text-muted-foreground">
            <div className="flex space-x-1">
              <div
                className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground"
                style={{ animationDelay: '0ms' }}
              ></div>
              <div
                className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground"
                style={{ animationDelay: '150ms' }}
              ></div>
              <div
                className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground"
                style={{ animationDelay: '300ms' }}
              ></div>
            </div>
            <span>
              {typingUsers.length === 1
                ? `${typingUsers[0].username} is typing...`
                : `${typingUsers.length} people are typing...`}
            </span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="border-t p-4">
        <div className="flex gap-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => {
              setNewMessage(e.target.value)
              if (e.target.value.trim()) {
                handleTyping()
              }
            }}
            placeholder={isConnected ? 'Type a message...' : 'Waiting for connection...'}
            className="flex-1 rounded-lg border bg-card px-4 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
            maxLength={5000}
            disabled={!isConnected || isSending}
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || !isConnected || isSending}
            className="rounded-lg bg-primary px-6 py-2 text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSending ? 'Sending...' : 'Send'}
          </button>
        </div>
      </form>
    </div>
  )
}
